class PumpkinPdpVideoCarouselElement extends HTMLElement {
  connectedCallback() {
    this.pumpkinTrack = this.querySelector('.pumpkin_pdp_video_carousel__track');
    this.pumpkinItems = Array.from(this.querySelectorAll('.pumpkin_pdp_video_carousel__item'));
    this.pumpkinDotsEl = this.querySelector('.pumpkin_pdp_video_carousel__dots');
    this.pumpkinControlsEl = this.querySelector('.pumpkin_pdp_video_carousel__controls');
    this.pumpkinPrevBtn = this.querySelector('.pumpkin_pdp_video_carousel__prev');
    this.pumpkinNextBtn = this.querySelector('.pumpkin_pdp_video_carousel__next');
    this.pumpkinActivePage = 0;
    this.pumpkinDots = [];

    if (!this.pumpkinTrack || !this.pumpkinItems.length) return;

    this.bindPumpkinCarouselEvents();
    this.bindPumpkinPlayEvents();

    requestAnimationFrame(() => {
      this.buildPumpkinDots();
      this.updatePumpkinControls();

      const pumpkinResizeObserver = new ResizeObserver(() => this.handlePumpkinResize());
      pumpkinResizeObserver.observe(this.pumpkinTrack);
    });
  }

  getPumpkinItemsPerView() {
    if (!this.pumpkinItems.length) return 1;

    const pumpkinStyle = getComputedStyle(this.pumpkinTrack);
    const pumpkinPaddingLeft = parseFloat(pumpkinStyle.paddingLeft) || 0;
    const pumpkinPaddingRight = parseFloat(pumpkinStyle.paddingRight) || 0;
    const pumpkinGap = parseFloat(pumpkinStyle.columnGap) || 0;
    const pumpkinVisibleWidth = this.pumpkinTrack.clientWidth - pumpkinPaddingLeft - pumpkinPaddingRight;
    const pumpkinItemWidth = this.pumpkinItems[0].offsetWidth;

    if (!pumpkinItemWidth) return 1;

    return Math.max(1, Math.round((pumpkinVisibleWidth + pumpkinGap) / (pumpkinItemWidth + pumpkinGap)));
  }

  getPumpkinPageCount() {
    return Math.max(1, Math.ceil(this.pumpkinItems.length / this.getPumpkinItemsPerView()));
  }

  buildPumpkinDots() {
    if (!this.pumpkinDotsEl) return;

    const pumpkinCount = this.getPumpkinPageCount();
    const pumpkinHide = pumpkinCount <= 1;

    this.pumpkinDotsEl.hidden = pumpkinHide;
    if (this.pumpkinControlsEl) this.pumpkinControlsEl.hidden = pumpkinHide;

    this.pumpkinDotsEl.innerHTML = '';
    this.pumpkinDots = [];

    for (let pumpkinIndex = 0; pumpkinIndex < pumpkinCount; pumpkinIndex++) {
      const pumpkinDotButton = document.createElement('button');

      pumpkinDotButton.className = 'pumpkin_pdp_video_carousel__dot';

      if (pumpkinIndex === 0) {
        pumpkinDotButton.classList.add('pumpkin_pdp_video_carousel__dot--active');
      }

      pumpkinDotButton.setAttribute('role', 'tab');
      pumpkinDotButton.setAttribute('aria-label', `Go to page ${pumpkinIndex + 1}`);
      pumpkinDotButton.setAttribute('aria-selected', pumpkinIndex === 0 ? 'true' : 'false');
      pumpkinDotButton.setAttribute('data-index', pumpkinIndex);
      pumpkinDotButton.type = 'button';

      this.pumpkinDotsEl.appendChild(pumpkinDotButton);
      this.pumpkinDots.push(pumpkinDotButton);
    }
  }

  handlePumpkinResize() {
    clearTimeout(this.pumpkinResizeTimer);

    this.pumpkinResizeTimer = setTimeout(() => {
      const pumpkinNewCount = this.getPumpkinPageCount();

      if (this.pumpkinDots.length !== pumpkinNewCount) {
        this.pumpkinActivePage = 0;
        this.buildPumpkinDots();
        this.updatePumpkinControls();
      }
    }, 150);
  }

  bindPumpkinCarouselEvents() {
    this.pumpkinPrevBtn?.addEventListener('click', () => this.navigatePumpkinCarousel(-1));
    this.pumpkinNextBtn?.addEventListener('click', () => this.navigatePumpkinCarousel(1));

    this.pumpkinDotsEl?.addEventListener('click', (event) => {
      const pumpkinDotButton = event.target.closest('.pumpkin_pdp_video_carousel__dot');

      if (pumpkinDotButton) {
        this.goToPumpkinPage(Number(pumpkinDotButton.dataset.index));
      }
    });

    let pumpkinScrollTimer;

    const handlePumpkinScroll = () => {
      if (this.isPumpkinProgrammaticScroll) return;

      clearTimeout(pumpkinScrollTimer);
      pumpkinScrollTimer = setTimeout(() => this.syncPumpkinActiveFromScroll(), 120);
    };

    this.pumpkinTrack.addEventListener('scroll', handlePumpkinScroll, { passive: true });

    this.pumpkinTrack.addEventListener('scrollend', () => {
      clearTimeout(pumpkinScrollTimer);

      const wasPumpkinProgrammatic = this.isPumpkinProgrammaticScroll;
      this.isPumpkinProgrammaticScroll = false;

      if (!wasPumpkinProgrammatic) {
        this.syncPumpkinActiveFromScroll();
      }
    });
  }

  navigatePumpkinCarousel(direction) {
    this.goToPumpkinPage(this.pumpkinActivePage + direction);
  }

  goToPumpkinPage(page) {
    const pumpkinCount = this.getPumpkinPageCount();
    const pumpkinClampedPage = Math.max(0, Math.min(page, pumpkinCount - 1));
    const pumpkinItemIndex = pumpkinClampedPage * this.getPumpkinItemsPerView();
    const pumpkinTargetItem = this.pumpkinItems[Math.min(pumpkinItemIndex, this.pumpkinItems.length - 1)];

    this.isPumpkinProgrammaticScroll = true;

    clearTimeout(this.pumpkinProgrammaticScrollTimer);
    this.pumpkinProgrammaticScrollTimer = setTimeout(() => {
      this.isPumpkinProgrammaticScroll = false;
    }, 800);

    pumpkinTargetItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });

    this.pumpkinActivePage = pumpkinClampedPage;
    this.updatePumpkinControls();
  }

  syncPumpkinActiveFromScroll() {
    const pumpkinTrackLeft = this.pumpkinTrack.getBoundingClientRect().left;
    let pumpkinClosestIndex = 0;
    let pumpkinClosestDistance = Infinity;

    this.pumpkinItems.forEach((item, index) => {
      const pumpkinDistance = Math.abs(item.getBoundingClientRect().left - pumpkinTrackLeft);

      if (pumpkinDistance < pumpkinClosestDistance) {
        pumpkinClosestDistance = pumpkinDistance;
        pumpkinClosestIndex = index;
      }
    });

    this.pumpkinActivePage = Math.floor(pumpkinClosestIndex / this.getPumpkinItemsPerView());
    this.updatePumpkinControls();
  }

  updatePumpkinControls() {
    const pumpkinCount = this.getPumpkinPageCount();

    this.pumpkinDots.forEach((dot, index) => {
      const pumpkinActive = index === this.pumpkinActivePage;

      dot.classList.toggle('pumpkin_pdp_video_carousel__dot--active', pumpkinActive);
      dot.setAttribute('aria-selected', String(pumpkinActive));
    });

    if (this.pumpkinPrevBtn) this.pumpkinPrevBtn.disabled = this.pumpkinActivePage === 0;
    if (this.pumpkinNextBtn) this.pumpkinNextBtn.disabled = this.pumpkinActivePage === pumpkinCount - 1;
  }

  bindPumpkinPlayEvents() {
    this.pumpkinItems.forEach((item) => {
      const pumpkinPlayButton = item.querySelector('.pumpkin_pdp_video_carousel__play-btn');

      pumpkinPlayButton?.addEventListener('click', () => this.playPumpkinVideo(item));
    });
  }

  playPumpkinVideo(item) {
    const pumpkinWrap = item.querySelector('.pumpkin_pdp_video_carousel__video-wrap');
    const { videoId, videoType } = pumpkinWrap.dataset;

    if (!videoType) return;

    if (videoType === 'cdn') {
      this.playPumpkinCdnVideo(pumpkinWrap);
      return;
    }

    if (!videoId) return;

    const pumpkinSrc = this.buildPumpkinEmbedUrl(videoId, videoType);
    if (!pumpkinSrc) return;

    const pumpkinIframe = document.createElement('iframe');
    pumpkinIframe.src = pumpkinSrc;
    pumpkinIframe.className = 'pumpkin_pdp_video_carousel__iframe';
    pumpkinIframe.setAttribute('frameborder', '0');
    pumpkinIframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
    pumpkinIframe.setAttribute('allowfullscreen', '');
    pumpkinIframe.setAttribute('title', 'Video player');

    const pumpkinUnmuteButton = this.buildPumpkinUnmuteButton(pumpkinIframe, videoType);

    pumpkinWrap.innerHTML = '';
    pumpkinWrap.appendChild(pumpkinIframe);
    pumpkinWrap.appendChild(pumpkinUnmuteButton);
  }

  playPumpkinCdnVideo(wrap) {
    const pumpkinVideo = wrap.querySelector('.pumpkin_pdp_video_carousel__native-video');
    const pumpkinPlayButton = wrap.querySelector('.pumpkin_pdp_video_carousel__play-btn');

    if (!pumpkinVideo) return;

    if (!pumpkinVideo.dataset.pumpkinVcInit) {
      pumpkinVideo.dataset.pumpkinVcInit = '1';

      wrap.querySelector('.pumpkin_pdp_video_carousel__cover')?.remove();
      pumpkinVideo.style.display = 'block';
      wrap.appendChild(this.buildPumpkinCdnUnmuteButton(pumpkinVideo));

      pumpkinVideo.addEventListener('play', () => this.setPumpkinPlayState(pumpkinPlayButton, true));
      pumpkinVideo.addEventListener('pause', () => this.setPumpkinPlayState(pumpkinPlayButton, false));
      pumpkinVideo.addEventListener('ended', () => this.setPumpkinPlayState(pumpkinPlayButton, false));
    }

    if (pumpkinVideo.paused) {
      pumpkinVideo.play().catch(() => {});
    } else {
      pumpkinVideo.pause();
    }
  }

  setPumpkinPlayState(playButton, playing) {
    if (!playButton) return;

    const pumpkinPlayIcon = playButton.querySelector('.pumpkin_pdp_video_carousel__icon-play');
    const pumpkinPauseIcon = playButton.querySelector('.pumpkin_pdp_video_carousel__icon-pause');

    if (pumpkinPlayIcon) pumpkinPlayIcon.hidden = playing;
    if (pumpkinPauseIcon) pumpkinPauseIcon.hidden = !playing;

    playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  }

  buildPumpkinCdnUnmuteButton(video) {
    let pumpkinIsMuted = true;

    const pumpkinButton = document.createElement('button');
    pumpkinButton.className = 'pumpkin_pdp_video_carousel__unmute-btn';
    pumpkinButton.type = 'button';
    pumpkinButton.setAttribute('aria-label', 'Unmute video');
    pumpkinButton.innerHTML = this.iconPumpkinMuted();

    pumpkinButton.addEventListener('click', () => {
      pumpkinIsMuted = !pumpkinIsMuted;
      video.muted = pumpkinIsMuted;
      pumpkinButton.innerHTML = pumpkinIsMuted ? this.iconPumpkinMuted() : this.iconPumpkinUnmuted();
      pumpkinButton.setAttribute('aria-label', pumpkinIsMuted ? 'Unmute video' : 'Mute video');
    });

    return pumpkinButton;
  }

  buildPumpkinEmbedUrl(id, type) {
    if (type === 'youtube') {
      return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&rel=0&playsinline=1&enablejsapi=1`;
    }

    if (type === 'vimeo') {
      return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&byline=0&portrait=0&title=0&api=1`;
    }

    return null;
  }

  buildPumpkinUnmuteButton(iframe, videoType) {
    let pumpkinIsMuted = true;

    const pumpkinButton = document.createElement('button');
    pumpkinButton.className = 'pumpkin_pdp_video_carousel__unmute-btn';
    pumpkinButton.type = 'button';
    pumpkinButton.setAttribute('aria-label', 'Unmute video');
    pumpkinButton.innerHTML = this.iconPumpkinMuted();

    pumpkinButton.addEventListener('click', () => {
      pumpkinIsMuted = !pumpkinIsMuted;
      this.postPumpkinMuteCommand(iframe, videoType, pumpkinIsMuted);
      pumpkinButton.innerHTML = pumpkinIsMuted ? this.iconPumpkinMuted() : this.iconPumpkinUnmuted();
      pumpkinButton.setAttribute('aria-label', pumpkinIsMuted ? 'Unmute video' : 'Mute video');
    });

    return pumpkinButton;
  }

  postPumpkinMuteCommand(iframe, type, mute) {
    if (type === 'youtube') {
      const pumpkinFunctionName = mute ? 'mute' : 'unMute';

      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: pumpkinFunctionName, args: [] }),
        '*'
      );
    } else if (type === 'vimeo') {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'setMuted', value: mute }),
        'https://player.vimeo.com'
      );
    }
  }

  iconPumpkinMuted() {
    return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="currentColor"/>
    </svg>`;
  }

  iconPumpkinUnmuted() {
    return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" fill="currentColor"/>
    </svg>`;
  }
}

if (!customElements.get('pumpkin-pdp-video-carousel')) {
  customElements.define('pumpkin-pdp-video-carousel', PumpkinPdpVideoCarouselElement);
}